import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';

import { useCreateClient } from '../contexts/clients';
import { EMPTY_VALUES, toPayload, validate } from '../utils/form';
import ClientFields from './fields';

import '../styles/form.css';

export default function CreateClientForm() {
    const navigate = useNavigate();
    const createClient = useCreateClient();

    const [values, setValues] = useState(EMPTY_VALUES);
    const [errors, setErrors] = useState({});

    const change = (field, value) => {
        setValues(prev => ({ ...prev, [field]: value }));
        // Clearing the field's error as it is typed: leaving it on screen while
        // the person fixes it reads as if the fix did not count.
        setErrors(prev => (prev[field] ? { ...prev, [field]: undefined } : prev));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        const found = validate(values);
        if (Object.keys(found).length) {
            setErrors(found);
            return;
        }

        try {
            const created = await createClient.mutateAsync(toPayload(values));
            toast.success('Cliente creado');
            // Straight to its edit form: right after creating, what is wanted is
            // the record, not the list it got lost in.
            navigate(`/admin/clients/${created.id}`, { replace: true });
        } catch (err) {
            // TSS vite/04 — the backend answers 400 with { error, fields }, and
            // `fields` is per input. Those go next to the input; anything else
            // is a toast.
            if (err.fields) setErrors(err.fields);
            else toast.error(err.message || 'No se pudo crear el cliente');
        }
    };

    return (
        <section className="client-form">
            <header className="client-form__header">
                <h2 className="client-form__title">Nuevo cliente</h2>
                <p className="client-form__subtitle">
                    Un cliente es para quién es el trabajo. Después cuelgan sus
                    proyectos. Solo el nombre es obligatorio; lo demás se completa
                    cuando se sepa.
                </p>
            </header>

            <form onSubmit={handleSubmit} noValidate>
                <div className="client-form__card">
                    <div className="client-form__section">
                        <h3 className="client-form__section-title">Datos del cliente</h3>
                        <p className="client-form__section-text">
                            Con quién se trabaja y cómo ubicarlo.
                        </p>
                    </div>

                    <ClientFields
                        values={values}
                        errors={errors}
                        onChange={change}
                        disabled={createClient.isPending}
                    />
                </div>

                <div className="client-form__actions">
                    <Link to="/admin/clients" className="client-form__cancel">Cancelar</Link>
                    <button
                        type="submit"
                        className="client-form__submit"
                        disabled={createClient.isPending}
                    >
                        {createClient.isPending ? 'Creando...' : 'Crear cliente'}
                    </button>
                </div>
            </form>
        </section>
    );
}
